import { useMutation, useQuery } from '@tanstack/react-query';
import { ListItem, Toggle, f7 } from 'framework7-react';
import { FC, Fragment, useEffect, useMemo, useState } from 'react';

import { useAppContext } from '@contexts/AppContext';
import { useEntertainerEngagementContext } from '@contexts/EntertainerEngagementContext';
import pluralize from 'pluralize';

import API from '@api/API';

import { Entertainer, useCurrentEntertainerQuery } from '@queries/EntertainerQueries';
import { useFeaturedLooksFeedPostsQuery } from '@queries/PostQueries';
import { useSeriesByTagSlug, useSeriesEntertainersQuery } from '@queries/TagQueries';
import { useCurrentUser } from '@queries/UserQueries';

import { useAnalytics } from '@hooks/useAnalytics';
import { useCitiesQuery, useCityById } from '@hooks/useCurrentUserLocation';

import { EntertainerDashboardAction, useLocation, useNavigate, useParams } from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';
import { mapStringToParagraphs } from '@utilities/mapStringToParagraphs';
import { openExternalUrl } from '@utilities/openExternalUrl';

import Button from '@components/buttons/Button';
import { ShareLinkButton } from '@components/buttons/ShareLinkButton';
import EventCard, {
  EventActions,
  EventHeader,
  EventImage,
  PromoterHeader,
} from '@components/cards/EventCard';
import { VenueCard } from '@components/cards/VenueCard';
import { ImageUploadField } from '@components/form/ImageUploadField';
import { Input } from '@components/form/Input';
import { Select } from '@components/form/Select';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { LineAwesomeIcon } from '@components/icons/LineAwesomeIcon';
import { AsyncImage } from '@components/layout/AsyncImg';
import { ButtonFilter } from '@components/layout/ButtonFilter/ButtonFilter';
import Card from '@components/layout/Card/Card';
import { useDialog } from '@components/layout/Dialog/useDialog';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { ErrorBoundary } from '@components/layout/ErrorBoundary';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { LinkBanner } from '@components/layout/LinkBanner/LinkBanner';
import { List } from '@components/layout/List/List';
import { Popup } from '@components/layout/Popup/Popup';
import { Screen } from '@components/layout/Screen/Screen';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { ShowsTabContent } from '@screens/CommunityScreen/CommunityScreen';
import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';
import { SeriesEntertainersList } from '@screens/series/SeriesScreen/SeriesScreen';
import {
  SweepstakesCard,
  useSweepstakesReferralLink,
} from '@screens/series/SeriesScreen/SweepstakesCard';

import { FeaturedKing } from '../EntertainerDetailsScreen/assets/FeaturedKing';
import { LandingPageSweepstakesCard } from '../EntertainerSweepstakesScreen/EntertainerSweepstakesScreen';
import { FeaturedLook, useFeaturedLooksQuery } from '../FeaturedLooksScreen/FeaturedLooksScreen';
import {
  AddFeaturedPhotoPopup,
  AddImageContent,
} from '../components/FeaturedPhotoManagementSection';
import { ScheduledSocialPost } from './ScheduledSocialPost';
import tickets from './tickets.svg';
import { useCityDragKingSeriesMostBookedEntertainers } from './useCityDragKingSeriesMostBookedEntertainers';

interface DragKingCommunityScreenProps {}

interface DragKingCommunitySubmission {
  _id: string;
  title: string;
  image?: string;
  status: string;
  entertainer?: {
    _id: string;
    title: string;
    bookingHandle: string;
  };
}

const useDragKingCommunitySubmissions = (isEnabled: boolean) => {
  const { data, isFetched } = useQuery({
    queryKey: ['dragKingCommunitySubmissions'],
    queryFn: () =>
      API.get<{ submissions: DragKingCommunitySubmission[] }>(
        `/v1/drag-king-community/submissions`,
      ),
    enabled: isEnabled,
  });

  return {
    submissions: data?.submissions,
    areSubmissionsReady: isFetched,
  };
};

const useUpdateDragKingCommunitySubmission = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({
      entertainerMediaId,
      status,
      temp,
    }: {
      entertainerMediaId: string;
      status: 'approved' | 'rejected';
      temp?: {
        entertainerInstagram?: string;
        base64Image?: any;
        path?: string;
        title?: string;
        addCollaborator?: boolean;
      };
    }) =>
      API.post(`/v1/drag-king-community/submissions`, {
        entertainerMediaId,
        status,
        temp,
      }),
  });

  return {
    isUpdatingSubmission: isPending,
    updateSubmissionAsync: mutateAsync,
  };
};

const DragKingSubmissionCard: FC<{
  submission: DragKingCommunitySubmission;
}> = ({ submission }) => {
  const [status, setStatus] = useState(submission.status);
  const [title, setTitle] = useState(submission.entertainer?.title);
  const { updateSubmissionAsync } = useUpdateDragKingCommunitySubmission();

  const handleApprove = async () => {
    setStatus('approved');
    await updateSubmissionAsync({
      entertainerMediaId: submission._id,
      status: 'approved',
      temp: {
        path: submission.image, // for the sake of the API, we need to send the image path
        title, // this is required for the API to update the title of the media
      },
    });
  };

  const handleReject = async () => {
    setStatus('rejected');
    await updateSubmissionAsync({
      entertainerMediaId: submission._id,
      status: 'rejected',
      temp: {
        path: submission.image, // for the sake of the API, we need to send the image path
        title, // this is required for the API to update the title of the media
      },
    });
  };

  return (
    <div className="d-grid gap-2">
      <img
        src={submission.image}
        alt={submission.title}
        className="w-100"
        style={{ borderRadius: 8 }}
      />
      <Input
        defaultValue={submission.entertainer.title}
        onChange={(e) => setTitle(e.target.value)}
      />
      <div className="d-flex gap-2">
        <Button
          roundness="rounded"
          color="neutral"
          variant={status === 'approved' ? 'default' : 'outlined'}
          onClick={handleApprove}
        >
          Approve
        </Button>
        <Button
          roundness="rounded"
          color="neutral"
          variant={status === 'rejected' ? 'default' : 'outlined'}
          onClick={handleReject}
        >
          Reject
        </Button>
      </div>
    </div>
  );
};

const AddCustomDragKingSubmissionPopupContent: FC<{ onClose: () => void }> = ({ onClose }) => {
  const [entertainerInstagram, setInstagram] = useState('');
  const [base64Image, setUpdatedImage] = useState<string | ArrayBuffer>();
  const [title, setTitle] = useState('');
  const [status, setStatus] = useState('approved');
  const [addCollaborator, setAddCollaborator] = useState(true);

  const { isUpdatingSubmission, updateSubmissionAsync } = useUpdateDragKingCommunitySubmission();

  const handleSubmit = async () => {
    await updateSubmissionAsync({
      entertainerMediaId: '',
      status: status as any,
      temp: {
        entertainerInstagram,
        base64Image,
        title,
        addCollaborator,
      },
    });

    onClose();
  };

  return (
    <div className="d-grid gap-2">
      <Select
        options={[
          { value: 'approved', label: 'Approved' },
          { value: 'pending', label: 'Pending' },
        ]}
        value={status}
        onChange={(e) => setStatus(e.target.value)}
      />
      <Input
        placeholder="Stage Name *"
        name="stageName"
        onChange={(event) => setTitle(event.target.value)}
      />
      <Input
        placeholder="Instagram *"
        name="instagram"
        onChange={(event) => setInstagram(event.target.value)}
      />
      <div className="d-flex gap-2 align-items-center">
        <Toggle defaultChecked={addCollaborator} onToggleChange={setAddCollaborator} />
        <div>
          <strong>Add Collaborator</strong>
        </div>
      </div>
      <Card>
        <ImageUploadField
          name="customSubmissionImage"
          placeholder="Select an image"
          onChange={(blob) => setUpdatedImage(blob)}
        />
      </Card>
      <div>
        <Button
          roundness="rounded"
          disabled={!base64Image || !title || isUpdatingSubmission}
          onClick={handleSubmit}
        >
          {isUpdatingSubmission ? 'Uploading...' : 'Upload'}
        </Button>
      </div>
    </div>
  );
};

const AddCustomDragKingSubmissionPopup: FC<{ isVisible: boolean; onClose: () => void }> = ({
  isVisible,
  onClose,
}) => {
  return (
    <Popup title="Add Custom" isVisible={isVisible} onClose={onClose} height="full">
      <Popup.Content>
        {isVisible && <AddCustomDragKingSubmissionPopupContent onClose={onClose} />}
      </Popup.Content>
    </Popup>
  );
};

const useShareEntertainerMediaPostMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({ entertainerMediaId, title }: { entertainerMediaId: string; title?: string }) =>
      API.post(`/v1/entertainer-media/${entertainerMediaId}/share-badges-post`, {
        title,
      }),
  });

  return {
    isSharing: isPending,
    shareEntertainerMediaPostAsync: mutateAsync,
  };
};

const BadgeCountCard: FC<{ featuredLook: FeaturedLook }> = ({ featuredLook }) => {
  const rootImageUrl = `https://gaggl.app/api/v1/entertainer-media/${featuredLook._id}/badges-post.jpg`;
  const [title, setTitle] = useState<string>();
  const [imageUrl, setImageUrl] = useState(rootImageUrl);
  const [isShared, setIsShared] = useState(false);

  const { shareEntertainerMediaPostAsync } = useShareEntertainerMediaPostMutation();

  const handleShare = async () => {
    if (!isShared) {
      setIsShared(true);
      await shareEntertainerMediaPostAsync({
        entertainerMediaId: featuredLook._id,
        title,
      });
    }
  };

  return (
    <div className="d-grid gap-2">
      <img src={imageUrl} alt={featuredLook.title} className="w-100" style={{ borderRadius: 8 }} />
      <Input defaultValue={featuredLook.title} onChange={(e) => setTitle(e.target.value)} />
      <div className="d-flex gap-2">
        <Button
          roundness="rounded"
          color="neutral"
          variant="outlined"
          onClick={() => {
            setImageUrl(`${rootImageUrl}?title=${encodeURIComponent(title)}`);
          }}
        >
          Update Title
        </Button>
        <Button roundness="rounded" variant="outlined" color="neutral" onClick={handleShare}>
          {isShared ? 'Shared' : 'Share Image'}
        </Button>
      </div>
    </div>
  );
};

const ShareBadgeCountContent = () => {
  const { featuredLooksAreReady, featuredLooks = [] } = useFeaturedLooksQuery({});

  const [end, setEnd] = useState(5);

  const kingsThingsLooks = useMemo(
    () => featuredLooks.filter(({ entertainer }) => entertainer.isDragKingThing),
    [featuredLooks],
  );

  return (
    <>
      <TitleToolbar text="Latest Badges Shared" size="sm" />

      <div className="d-grid gap-4">
        {kingsThingsLooks.slice(0, Math.min(end, kingsThingsLooks.length)).map((featuredLook) => (
          <BadgeCountCard key={featuredLook._id} featuredLook={featuredLook} />
        ))}
        <Button
          roundness="rounded"
          variant="smooth"
          color="neutral"
          onClick={() => setEnd((prevValue) => prevValue + 5)}
        >
          Load More
        </Button>
      </div>
    </>
  );
};

const EntertainerAvatar: FC<{ entertainer: Entertainer }> = ({ entertainer }) => {
  const image = entertainer.avatar || entertainer.featuredMedia?.[0]?.image;
  const imageSize = 32;
  return (
    <div
      style={{
        position: 'relative',
        width: imageSize,
        height: imageSize,
      }}
    >
      <div style={{ borderRadius: 4, overflow: 'hidden', width: '100%', height: '100%' }}>
        {image ? (
          <AsyncImage
            src={image}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            placeholder={
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  background: 'rgba(255,255,255,.2)',
                }}
              />
            }
          />
        ) : (
          <div style={{ width: '100%', height: '100%', background: 'rgba(255,255,255,.2)' }} />
        )}
      </div>
    </div>
  );
};

const SHARE_OPTIONS = [
  { value: 'join', label: 'Entertainers' },
  { value: 'look', label: 'Add Look' },
  // { value: 'badge', label: 'Reward Badges' },
  { value: 'show', label: 'Add Show' },
];

interface MostBookedEntertainersScreenProps {
  cityId: string;
}

export const MostBookedEntertainersScreen =
  createScreenComponent<MostBookedEntertainersScreenProps>(({ cityId, back, f7route }) => {
    const { openGatedAddShowPopup } = useEntertainerEngagementContext();
    const { track } = useAnalytics();
    const navigate = useNavigate();
    const [didSubmitCity, setDidSubmitCity] = useState(!!f7route.query?.cityId);
    const { currentUser, refetchUser } = useCurrentUser();
    const { entertainer } = useCurrentEntertainerQuery();
    const { cities = [] } = useCitiesQuery();
    const { city } = useCityById(cityId ?? entertainer?.cityIds?.[0]);
    const { submissions = [] } = useDragKingCommunitySubmissions(!!entertainer?._id);
    const { createAuthGateHandler } = useAuthGate();
    const [addImageModeIsVisible, setAddImageModeIsVisible] = useState(
      f7route.query?.addImageModeIsVisible === 'true',
    );
    const [addCustomSubmissionModeIsVisible, setAddCustomSubmissionModeIsVisible] = useState(false);
    const [lookOrShow, setLookOrShow] = useState(entertainer?._id ? 'look' : 'join');
    const [tab, setTab] = useState(f7route?.query?.tab ?? 'performers');
    const { openConfirmDialog } = useDialog();
    const { castEntertainers } = useCityDragKingSeriesMostBookedEntertainers(cityId);

    const { isFetched: isSeriesReady, series } = useSeriesByTagSlug(
      city?.featuredSeries?.kingsThings,
    );

    const handleAddEvent = () => {
      openGatedAddShowPopup({
        cityId,
        isEntertainerOptional: true,
      });
    };

    const currentPath = `/noauth/drag-king-shoutouts/${cityId}/most-booked?tab=${tab}`;

    return (
      <Screen
        name="Drag King Community Screen"
        hideToolbar
        headerProps={{
          back: { to: `/noauth/drag-king-shoutouts/?cityId=${cityId}` },
          title: <Screen.GagglTitle />,
          right: <ShareLinkButton url={`https://gaggl.app/drag-shoutouts`} />,
        }}
      >
        <Screen.Content>
          <div className="d-flex mb-2 gap-3 align-items-center">
            <div>
              <div
                style={{
                  textTransform: 'uppercase',
                  marginBottom: -4,
                  opacity: 0.7,
                  letterSpacing: '.1rem',
                }}
              >
                <strong>{city?.name}</strong>
              </div>
              <div>
                <strong className="fs-5">Booked Performers & Their Shows</strong>
              </div>
            </div>
          </div>

          <p className="mt-0">
            <strong>Gaggl</strong> is all about building community, and we want to make it super
            easy to find local drag kings & things and their shows. This is a list of the most
            booked performers and their upcoming shows.
          </p>

          <p>
            Don't see your name or your show? No problem! Add your own show to the list or add
            yourself to the cast list.
          </p>

          <ButtonFilter
            selected={tab}
            options={[
              { value: 'performers', label: 'Performers' },
              { value: 'shows', label: 'Shows' },
            ]}
            onChange={setTab}
          />

          {tab === 'performers' && (
            <>
              <TitleToolbar
                text="Booked Kings & Things"
                action={<span>{`${castEntertainers.length} cast`}</span>}
                size="md"
              />
              <List
                className="mt-0"
                listItems={castEntertainers.map((entertainer) => ({
                  startIcon: <EntertainerAvatar entertainer={entertainer} />,
                  title: entertainer.title,
                  subTitle: `${entertainer.events.length} Upcoming ${pluralize('Show', entertainer.events.length)}`,
                  onClick: () => {
                    navigate(`/noauth/entertainer/${entertainer.bookingHandle}`, {
                      state: { back: currentPath },
                    });
                  },
                }))}
              />
            </>
          )}

          {tab === 'shows' && (
            <>
              <p className="mt-0">
                <strong>NOTE:</strong>{' '}
                <span>
                  Use the&nbsp; (<LineAwesomeIcon name="ellipsis-h" />) &nbsp;if you're in the cast
                  of the show.
                </span>
              </p>
              <ShowsTabContent
                hideOpenStages
                cityId={cityId}
                tagIds={[city?.featuredSeries?.drag, series._id].filter(Boolean)}
                onAddEvent={handleAddEvent}
                back={currentPath}
              />
            </>
          )}
        </Screen.Content>
        <AddCustomDragKingSubmissionPopup
          isVisible={addCustomSubmissionModeIsVisible}
          onClose={() => setAddCustomSubmissionModeIsVisible(false)}
        />
      </Screen>
    );
  });
